import { useCallback, useEffect, useMemo, useRef, useState, type BaseSyntheticEvent } from 'react';
import { useForm, useWatch, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { normalizeAmountString } from '@shared/components/form/config-value';
import { motorplanPaymentSchema, type MotorplanPaymentValues } from '../schemas/motorplanPayment.schema';
import { sendPaymentLink } from '../services/paymentLinks';
import type { PaymentLinkInput, PaymentLinkSummary, SentPaymentLink } from '../types/paymentLinks.types';
import { isJustificationRequired } from '../utils/accessRefusal';
import { isPayOffAmount, payOffMinorFrom } from '../utils/motorplanAmount';
import { motorplanSubmitErrors, type MotorplanSubmitErrors } from '../utils/motorplanPaymentErrors';
import { formatMinor } from '../utils/topUpAmount';

export interface MotorplanPaymentFormOptions {
    partPaymentOffered?: boolean;
    leftToPay?: string;
}

export interface MotorplanPaymentFormState {
    control: Control<MotorplanPaymentValues>;
    paysOff: boolean;
    payOffOnly: boolean;
    confirming: boolean;
    cancelConfirm: () => void;
    confirmPayOff: (event?: BaseSyntheticEvent) => Promise<void>;
    isSubmitting: boolean;
    canSubmit: boolean;
    sentLink: SentPaymentLink | null;
    replacement: PaymentLinkSummary | null;
    replacing: boolean;
    confirmReplace: () => Promise<void>;
    keepExisting: () => void;
    resend: (live: PaymentLinkSummary) => void;
    formError: string | null;
    resendError: string | null;
    reset: () => void;
    submit: (event?: BaseSyntheticEvent) => Promise<void>;
}

interface PendingReplacement {
    input: PaymentLinkInput;
    live: PaymentLinkSummary;
    fromResend: boolean;
}

const emptyValues = (amountGross: string): MotorplanPaymentValues => ({
    amountGross,
    sendViaEmail: false,
    sendViaSms: false,
});

const resendInput = (live: PaymentLinkSummary, leftToPay: string): PaymentLinkInput => {
    const channels = { sendViaEmail: live.channels.email, sendViaSms: live.channels.sms };
    return live.paysOff
        ? { payOff: true, leftToPay, ...channels }
        : { amountGross: normalizeAmountString(live.amount) ?? live.amount, ...channels };
};

const firstMessage = ({ formError, fieldErrors }: MotorplanSubmitErrors): string | null =>
    formError ?? Object.values(fieldErrors).find((message) => message !== undefined) ?? null;

export function useMotorplanPaymentForm(
    agreementId: string,
    minimumMinor: number,
    maximumMinor: number,
    onSent?: (link: SentPaymentLink) => void,
    onAccessRefused?: () => void,
    { partPaymentOffered = true, leftToPay = '' }: MotorplanPaymentFormOptions = {},
): MotorplanPaymentFormState {
    const [confirming, setConfirming] = useState(false);
    const payOffMinor = payOffMinorFrom(leftToPay);
    const payOffOnly = !partPaymentOffered;
    const defaultAmount = payOffOnly && leftToPay !== '' ? leftToPay : formatMinor(minimumMinor);
    const schema = useMemo(
        () => motorplanPaymentSchema({ minimumMinor, maximumMinor, payOffMinor }),
        [minimumMinor, maximumMinor, payOffMinor],
    );
    const resolver = useMemo(() => zodResolver(schema), [schema]);
    const [sentLink, setSentLink] = useState<SentPaymentLink | null>(null);
    const [replacing, setReplacing] = useState(false);
    const replacingRef = useRef(false);
    const [replacement, setReplacement] = useState<PendingReplacement | null>(null);
    const [resendError, setResendError] = useState<string | null>(null);
    const generation = useRef(0);
    const latestDefaultAmount = useRef(defaultAmount);
    const latestAgreementId = useRef(agreementId);
    const {
        control,
        handleSubmit,
        setValue,
        setError,
        clearErrors,
        reset: resetForm,
        formState: { dirtyFields, isSubmitting, errors },
    } = useForm<MotorplanPaymentValues>({ resolver, mode: 'onTouched', defaultValues: emptyValues(defaultAmount) });
    const [amountGross, sendViaEmail, sendViaSms] = useWatch({ control, name: ['amountGross', 'sendViaEmail', 'sendViaSms'] });
    const complete = useMemo(
        () => schema.safeParse({ amountGross, sendViaEmail, sendViaSms }).success,
        [schema, amountGross, sendViaEmail, sendViaSms],
    );

    useEffect(() => {
        latestDefaultAmount.current = defaultAmount;
        if (!dirtyFields.amountGross) setValue('amountGross', defaultAmount);
    }, [defaultAmount, dirtyFields.amountGross, setValue]);

    const reset = useCallback(() => {
        generation.current += 1;
        resetForm(emptyValues(latestDefaultAmount.current));
        setSentLink(null);
        setReplacement(null);
        setResendError(null);
        setConfirming(false);
    }, [resetForm]);

    useEffect(() => {
        if (latestAgreementId.current === agreementId) return;
        latestAgreementId.current = agreementId;
        reset();
    }, [agreementId, reset]);

    const showOnForm = (errors: MotorplanSubmitErrors) => {
        Object.entries(errors.fieldErrors).forEach(([field, message], index) => {
            setError(field as keyof MotorplanPaymentValues, { message }, { shouldFocus: index === 0 });
        });
        if (errors.formError) setError('root.server', { message: errors.formError });
    };

    const dispatch = async (
        input: PaymentLinkInput,
        settleReplacement: (next: PendingReplacement | null) => void = setReplacement,
        fromResend = false,
    ) => {
        const submittedGeneration = generation.current;
        const isStale = () => submittedGeneration !== generation.current || agreementId !== latestAgreementId.current;
        setSentLink(null);
        setResendError(null);
        if (!input.replaceLive) setReplacement(null);
        let sent: SentPaymentLink;
        try {
            sent = await sendPaymentLink(agreementId, input);
        } catch (error) {
            if (isJustificationRequired(error, agreementId)) onAccessRefused?.();
            if (isStale()) return;
            const errors = motorplanSubmitErrors(error);
            // A resend can run with the form unmounted, so its failure is shown beside the awaiting notice.
            if (fromResend) setResendError(firstMessage(errors));
            else showOnForm(errors);
            settleReplacement(errors.liveLink ? { input, live: errors.liveLink, fromResend } : null);
            return;
        }
        onSent?.(sent);
        if (isStale()) return;
        settleReplacement(null);
        setSentLink(sent);
        resetForm(emptyValues(latestDefaultAmount.current));
    };

    const submit = handleSubmit(async (values) => {
        if (isPayOffAmount(values.amountGross, payOffMinor)) {
            setConfirming(true);
            return;
        }
        await dispatch(values);
    });

    const confirmPayOff = handleSubmit(async ({ sendViaEmail, sendViaSms }) => {
        try {
            await dispatch({ payOff: true, leftToPay, sendViaEmail, sendViaSms });
        } finally {
            setConfirming(false);
        }
    });

    const resend = useCallback(
        (live: PaymentLinkSummary) => {
            clearErrors();
            setResendError(null);
            setReplacement({ input: resendInput(live, leftToPay), live, fromResend: true });
        },
        [clearErrors, leftToPay],
    );

    const confirmReplace = async () => {
        if (!replacement || replacingRef.current) return;
        replacingRef.current = true;
        setReplacing(true);
        let settled: PendingReplacement | null | undefined;
        try {
            await dispatch({ ...replacement.input, replaceLive: true }, (next) => (settled = next), replacement.fromResend);
        } finally {
            // Closing in the same render that re-enables the opener lets the dialog restore focus to it.
            replacingRef.current = false;
            setReplacing(false);
            if (settled !== undefined) setReplacement(settled);
        }
    };

    return {
        control,
        paysOff: isPayOffAmount(amountGross, payOffMinor),
        payOffOnly,
        confirming,
        cancelConfirm: () => setConfirming(false),
        confirmPayOff,
        isSubmitting: isSubmitting || replacing,
        canSubmit: complete && !isSubmitting && !replacing,
        sentLink,
        replacement: replacement?.live ?? null,
        replacing,
        confirmReplace,
        keepExisting: () => setReplacement(null),
        resend,
        formError: errors.root?.server?.message ?? null,
        resendError,
        reset,
        submit,
    };
}
